import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { labelSongSections, saveEditedSections } from '../../../../services/SongSectionsService';
import { lyricsTextOf, needsDetection, sectionsForSong, GENERIC_SECTIONS } from '../../../../services/songSectionsModel';

/**
 * Song sections for the Lesson Dashboard (2026-09-25).
 *
 * - DETECTS ONCE: when the current song has lyrics and no current sections, ask
 *   the server (functions/songSections.js). The result is stored on the
 *   repertoire row, so the next open is free. A per-song ref stops a failure
 *   from re-firing on every render; "Detect again" is the retry.
 * - THE ACTIVE SECTION IS STICKY PER SONG: tap "Chorus" and every jot lands
 *   there until another chip is tapped. `null` = the whole song (the default,
 *   and what every note written before this feature carries).
 * - Rename saves straight to the row as 'edited', which the server then keeps.
 */
const useSongSections = ({ board }) => {
	const { currentSong, setSongs, isPersistent } = board;
	const [activeBySong, setActiveBySong] = useState({});
	const [status, setStatus] = useState({}); // songId -> 'detecting' | 'error'
	const tried = useRef(new Set());

	const sections = useMemo(() => sectionsForSong(currentSong), [currentSong]);
	const isGeneric = sections === GENERIC_SECTIONS;
	const songId = currentSong?.id || null;

	const patchSong = useCallback(
		(id, patch) => setSongs((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s))),
		[setSongs]
	);

	const detect = useCallback(
		async (id, force) => {
			setStatus((m) => ({ ...m, [id]: 'detecting' }));
			try {
				const res = await labelSongSections(id, { force });
				patchSong(id, { sections: res.sections, sectionsMeta: res.sectionsMeta });
				setStatus((m) => ({ ...m, [id]: undefined }));
				return true;
			} catch (e) {
				console.error('Error labelling song sections:', e);
				setStatus((m) => ({ ...m, [id]: 'error' }));
				return false;
			}
		},
		[patchSong]
	);

	useEffect(() => {
		if (!isPersistent || !currentSong || !needsDetection(currentSong)) return;
		if (tried.current.has(currentSong.id)) return;
		tried.current.add(currentSong.id);
		detect(currentSong.id, false);
	}, [currentSong, isPersistent, detect]);

	// A chip that no longer exists (re-detected, renamed away) falls back to the whole song.
	const storedActive = songId ? activeBySong[songId] : null;
	const activeSection = sections.find((s) => s.id === storedActive) || null;

	const setActive = useCallback(
		(sectionId) => {
			if (!songId) return;
			setActiveBySong((m) => ({ ...m, [songId]: sectionId || null }));
		},
		[songId]
	);

	const detectAgain = useCallback(() => (songId ? detect(songId, true) : Promise.resolve(false)), [songId, detect]);

	const rename = useCallback(
		async (nextSections) => {
			if (!songId) return false;
			const previous = { sections: currentSong.sections, sectionsMeta: currentSong.sectionsMeta };
			const text = lyricsTextOf(currentSong.lyrics);
			patchSong(songId, { sections: nextSections });
			try {
				const saved = await saveEditedSections(songId, nextSections, text);
				patchSong(songId, saved);
				return true;
			} catch (e) {
				console.error('Error saving section names:', e);
				patchSong(songId, previous);
				return false;
			}
		},
		[songId, currentSong, patchSong]
	);

	return {
		sections,
		isGeneric,
		activeSection,
		setActive,
		status: songId ? status[songId] || null : null,
		canDetect: Boolean(isPersistent && songId && lyricsTextOf(currentSong?.lyrics).trim()),
		detectAgain,
		rename,
	};
};

export default useSongSections;
